import React from 'react';
import { Box, Button, Stack, Typography, Divider } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';
import dayjs from 'dayjs';
import appConfig from '../../../config/appConfig';
import SignupBrandLogos from './SignupBrandLogos';

const WaitingPage = () => {
  const location = useLocation();
  const { email, submittedAt } = location.state || {};

  const formattedDate = submittedAt
    ? dayjs(submittedAt).format('DD/MM/YYYY, HH:mm:ss')
    : dayjs().format('DD/MM/YYYY, HH:mm:ss');

  return (
    <Stack spacing={4} sx={{ alignItems: 'center', width: '100%', maxWidth: '600px', mx: 'auto', py: 4 }}>
      <SignupBrandLogos variant="waiting" />

      <Box sx={{ width: '100%', mb: 4, position: 'relative' }}>
        <Stack
          direction="row"
          sx={{
            alignItems: 'flex-start',
            width: '100%',
            justifyContent: 'space-between',
            position: 'relative',
          }}
        >
          <Stack sx={{ alignItems: 'center', flex: 1, position: 'relative', zIndex: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'success.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
              }}
            >
              <CheckCircleIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'center', fontSize: '0.75rem' }}>
              Request Received
            </Typography>
          </Stack>

          <Stack sx={{ alignItems: 'center', flex: 1, position: 'relative', zIndex: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  opacity: 0.3,
                  animation: 'pulse 2s infinite',
                },
                '@keyframes pulse': {
                  '0%': {
                    transform: 'scale(1)',
                    opacity: 0.7,
                  },
                  '50%': {
                    transform: 'scale(1.1)',
                    opacity: 0.4,
                  },
                  '100%': {
                    transform: 'scale(1.2)',
                    opacity: 0,
                  },
                },
              }}
            >
              <AccessTimeIcon sx={{ color: 'white', fontSize: 28, position: 'relative', zIndex: 1 }} />
            </Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, textAlign: 'center', color: 'primary.main', fontSize: '0.75rem' }}
            >
              Under Review
            </Typography>
          </Stack>

          <Stack sx={{ alignItems: 'center', flex: 1, position: 'relative', zIndex: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'grey.300',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
              }}
            >
              <LockIcon sx={{ color: 'grey.600', fontSize: 28 }} />
            </Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, textAlign: 'center', color: 'text.secondary', fontSize: '0.75rem' }}
            >
              Account Activated
            </Typography>
          </Stack>

          <Box
            sx={{
              position: 'absolute',
              top: '24px',
              left: '24px',
              right: '24px',
              height: 2,
              zIndex: 1,
              display: 'flex',
            }}
          >
            <Box
              sx={{
                flex: 1,
                height: '100%',
                backgroundColor: 'success.main',
              }}
            />
            <Box
              sx={{
                flex: 1,
                height: '100%',
                backgroundColor: 'grey.300',
              }}
            />
          </Box>
        </Stack>
      </Box>

      <Stack spacing={2} sx={{ width: '100%', textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Your activation request is under review.
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          {appConfig.brandName} is a managed platform. We are verifying your organization details. Estimated time: 2-4
          business hours. Once approved, you will receive an email to set up your password and access your account.
        </Typography>

        <Box
          sx={{
            backgroundColor: 'grey.100',
            borderRadius: 1,
            p: 2,
            textAlign: 'left',
            border: '1px solid',
            borderColor: 'grey.300',
          }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Request submitted for:</strong> {email || 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Submitted on:</strong> {formattedDate}
          </Typography>
        </Box>
      </Stack>

      <Stack spacing={2} sx={{ width: '100%', mt: 2 }}>
        <Divider sx={{ my: 2 }}>
          <Typography variant="body2">Trouble logging in?</Typography>
        </Divider>

        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 2,
            width: '100%',
          }}
        >
          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            href={`mailto:${appConfig.brandEmail}`}
            sx={{
              borderColor: 'grey.300',
              color: 'text.primary',
              backgroundColor: 'white',
              textTransform: 'none',
              px: 3,
              py: 1.5,
              '&:hover': {
                borderColor: 'grey.400',
                backgroundColor: 'grey.50',
              },
            }}
          >
            Contact Support
          </Button>
        </Stack>
      </Stack>

      <Stack spacing={1} sx={{ width: '100%', mt: 2 }}>
        <Typography variant="body2" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ textDecoration: 'none', color: 'primary.main', fontWeight: 500 }}>
            Sign In
          </Link>
        </Typography>
      </Stack>
    </Stack>
  );
};

export default WaitingPage;
